import React, { useMemo, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import qs from "querystring";
import CardShell from "./CardShell";
import {
    ResponsiveContainer,
    AreaChart,
    Area,
    XAxis,
    YAxis,
    Tooltip as ReTooltip,
    Legend,
    CartesianGrid,
    ReferenceArea,
} from "recharts";
import { buildDays, realPointCount } from "./utils";

const Y_PAD_RATIO = 0.06;
const CHART_HEIGHT = 200;

function composeChartData(dayTicks, seriesDefs) {
    const seriesNames = seriesDefs.map((s) => s.label);
    const lookups = seriesDefs.map((s) => new Map((s.series || []).map((p) => [p.dayKey, p.value])));
    return dayTicks.map(({ key, label }) => {
        const ts = new Date(key + "T00:00:00Z").getTime();
        const datum = { ts, label };
        for (let i = 0; i < seriesNames.length; i++) {
            const v = lookups[i].get(key);
            datum[seriesNames[i]] = typeof v === "number" && Number.isFinite(v) ? v : null;
        }
        return datum;
    });
}

function computeYDomain(chartData, seriesNames) {
    let max = 0;
    for (const d of chartData) {
        for (const name of seriesNames) {
            const v = d[name];
            if (typeof v === "number" && Number.isFinite(v) && v > max) max = v;
        }
    }
    if (!Number.isFinite(max) || max <= 0) return [0, 1];
    return [0, max + Math.max(1, Math.round(max * Y_PAD_RATIO))];
}

function tsToYMD(ts) {
    const d = new Date(ts);
    const y = d.getUTCFullYear();
    const m = String(d.getUTCMonth() + 1).padStart(2, "0");
    const day = String(d.getUTCDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
}

function CustomTooltip({ active, payload, label }) {
    if (!active || !payload || !payload.length) return null;
    const dateLabel = payload[0]?.payload?.label || "";
    return (
        <div style={{
            background: "#fff",
            border: "1px solid #e5e7eb",
            borderRadius: 10,
            padding: "10px 14px",
            boxShadow: "0 4px 16px rgba(0,0,0,.10)",
            minWidth: 140,
        }}>
            <div style={{ fontSize: 11, color: "#9ca3af", marginBottom: 6 }}>{dateLabel}</div>
            {payload.map((p) => (
                <div key={p.name} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 3 }}>
                    <span style={{
                        display: "inline-block", width: 10, height: 10,
                        borderRadius: "50%", background: p.stroke, flexShrink: 0,
                    }} />
                    <span style={{ fontSize: 12, color: "#374151", flex: 1 }}>{p.name}</span>
                    <span style={{ fontSize: 12, fontWeight: 700, color: "#111" }}>
                        {p.value != null ? p.value.toLocaleString() : "—"}
                    </span>
                </div>
            ))}
        </div>
    );
}

export default function CombinedTrendCard({
    title,
    series,
    startDate,
    endDate,
    tooltip,
    showLegend = false,
    remountKey,
    children,
}) {
    const history  = useHistory();
    const location = useLocation();

    const [dragFrom, setDragFrom] = useState(null);
    const [dragTo,   setDragTo]   = useState(null);
    const dragRef = useRef(null);

    const seriesDefs = Array.isArray(series)
        ? series.filter((s) => s && typeof s.label === "string" && s.label.length)
        : [];

    const dayTicks    = buildDays(startDate, endDate, []);
    const seriesNames = seriesDefs.map((s) => s.label);
    const chartData   = composeChartData(dayTicks, seriesDefs);
    const yDomain     = computeYDomain(chartData, seriesNames);

    // Need at least one series with 2+ real points to draw a line. Otherwise we'd ship
    // an empty axes frame (1 point with dot={false} is invisible) — show a clear caption
    // instead of pretending the chart rendered.
    const maxRealPoints = Math.max(0, ...seriesDefs.map((s) => realPointCount(s.series)));

    const tickFormatter = useMemo(() => {
        const fmt = new Intl.DateTimeFormat(undefined, { day: "2-digit", month: "short", timeZone: "UTC" });
        return (ts) => fmt.format(new Date(ts));
    }, []);

    const writeRange = (fromTs, toTs) => {
        const [s, e] = fromTs < toTs ? [fromTs, toTs] : [toTs, fromTs];
        const current = qs.parse(location.search.replace(/^\?/, ""));
        delete current.range;
        current.start = tsToYMD(s);
        current.end   = tsToYMD(e);
        history.replace({ search: "?" + qs.stringify(current) });
    };

    const onMouseDown = (e) => {
        if (!e?.activeLabel) return;
        dragRef.current = e.activeLabel;
        setDragFrom(e.activeLabel);
        setDragTo(null);
    };

    const onMouseMove = (e) => {
        if (!dragRef.current || !e?.activeLabel) return;
        setDragTo(e.activeLabel);
    };

    const onMouseUp = () => {
        const from = dragRef.current;
        dragRef.current = null;
        if (from != null && dragTo != null && Math.abs(dragTo - from) > 86400000) {
            writeRange(from, dragTo);
        }
        setDragFrom(null);
        setDragTo(null);
    };

    const isDragging = dragFrom !== null && dragTo !== null;

    const autoKey =
        (startDate || "") + "|" + (endDate || "") + "|" + seriesNames.join("|") + "|" + yDomain.join("|") + "|" + chartData.length;
    const chartKey = remountKey ?? autoKey;
    // chartKey can contain spaces/parens (from series labels), which break SVG `url(#id)` fill
    // references and make the area fall back to solid black. Strip to id-safe characters.
    const gradKey = String(chartKey).replace(/[^a-zA-Z0-9_-]/g, "");

    if (maxRealPoints < 2) {
        return (
            <CardShell title={title} tooltip={tooltip} footer={children} minHeight={Math.max(360, CHART_HEIGHT + 100)}>
                <div style={{
                    width: "100%", height: CHART_HEIGHT,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    color: "#94a3b8", fontSize: 13,
                }}>
                    Not enough history yet to draw a trend
                </div>
            </CardShell>
        );
    }

    return (
        <CardShell title={title} tooltip={tooltip} footer={children} minHeight={Math.max(360, CHART_HEIGHT + 100)}>
            <div style={{ width: "100%", minWidth: 0, height: CHART_HEIGHT }}>
                <ResponsiveContainer width="99%" height="100%">
                    <AreaChart
                        key={chartKey}
                        data={chartData}
                        margin={{ top: 4, right: 4, bottom: 0, left: 0 }}
                        onMouseDown={onMouseDown}
                        onMouseMove={onMouseMove}
                        onMouseUp={onMouseUp}
                        style={{ cursor: isDragging ? "col-resize" : "crosshair" }}
                    >
                        <defs>
                            {seriesDefs.map((s, i) => (
                                <linearGradient key={i} id={`grad-${gradKey}-${i}`} x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%"  stopColor={s.stroke || "#1976d2"} stopOpacity={0.18} />
                                    <stop offset="95%" stopColor={s.stroke || "#1976d2"} stopOpacity={0} />
                                </linearGradient>
                            ))}
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                        <XAxis
                            dataKey="ts"
                            type="number"
                            scale="time"
                            domain={["dataMin", "dataMax"]}
                            tickFormatter={tickFormatter}
                            minTickGap={40}
                            tick={{ fontSize: 11, fill: "#9ca3af" }}
                            axisLine={{ stroke: "#e5e7eb" }}
                            tickLine={false}
                        />
                        <YAxis
                            domain={yDomain}
                            allowDecimals={false}
                            tick={{ fontSize: 11, fill: "#9ca3af" }}
                            axisLine={false}
                            tickLine={false}
                            width={52}
                        />
                        <ReTooltip
                            content={isDragging ? null : <CustomTooltip />}
                            cursor={{ stroke: "#e5e7eb", strokeWidth: 1 }}
                        />
                        {showLegend && (
                            <Legend
                                verticalAlign="bottom"
                                height={24}
                                iconType="circle"
                                iconSize={8}
                                wrapperStyle={{ fontSize: 12 }}
                            />
                        )}
                        {isDragging && (
                            <ReferenceArea
                                x1={Math.min(dragFrom, dragTo)}
                                x2={Math.max(dragFrom, dragTo)}
                                fill="#6366f1"
                                fillOpacity={0.15}
                                stroke="#6366f1"
                                strokeOpacity={0.6}
                                strokeWidth={1}
                            />
                        )}
                        {seriesDefs.map((s, i) => (
                            <Area
                                key={s.label}
                                type="natural"
                                dataKey={s.label}
                                stroke={s.stroke || "#1976d2"}
                                strokeWidth={2}
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                fill={`url(#grad-${gradKey}-${i})`}
                                fillOpacity={1}
                                dot={false}
                                connectNulls
                                isAnimationActive={false}
                            />
                        ))}
                    </AreaChart>
                </ResponsiveContainer>
            </div>
        </CardShell>
    );
}
